import { useEffect, useState } from 'react';
import { Link as RouterLink, NavLink, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import NcplLogo from '../brand/NcplLogo';
import { cta, mainNavigation } from '../../config/navigation';
import { tokens } from '../../theme/tokens';

function MenuIcon({ open }) {
  return (
    <Box
      component="span"
      sx={{
        width: 22,
        height: 14,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ height: 2, bgcolor: 'primary.main', transform: open ? 'translateY(6px) rotate(45deg)' : 'none' }} />
      <Box sx={{ height: 2, bgcolor: 'primary.main', opacity: open ? 0 : 1 }} />
      <Box sx={{ height: 2, bgcolor: 'primary.main', transform: open ? 'translateY(-6px) rotate(-45deg)' : 'none' }} />
    </Box>
  );
}

const navLinkSx = {
  fontSize: 12.5,
  fontWeight: 500,
  letterSpacing: '0.04em',
  color: tokens.muted,
  textDecoration: 'none',
  py: 1,
  whiteSpace: 'nowrap',
  '&:hover': { color: tokens.navy },
  '&.active': {
    color: tokens.navy,
    fontWeight: 700,
  },
};

function isCurrent(pathname, to) {
  return pathname === to;
}

function DesktopItem({ item }) {
  const location = useLocation();
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const active = item.children
    ? item.children.some((child) => isCurrent(location.pathname, child.to))
    : isCurrent(location.pathname, item.to);

  if (!item.children) {
    return (
      <Box component={NavLink} to={item.to} sx={{ ...navLinkSx, color: active ? tokens.navy : tokens.charcoal }}>
        {item.label}
      </Box>
    );
  }

  return (
    <>
      <Button
        id={`${item.label}-button`}
        aria-controls={open ? `${item.label}-menu` : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          ...navLinkSx,
          minHeight: 44,
          px: 0.5,
          color: active ? tokens.navy : tokens.muted,
          fontWeight: active ? 700 : 500,
          borderRadius: 0,
        }}
      >
        {item.label}
      </Button>
      <Menu
        id={`${item.label}-menu`}
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        MenuListProps={{ 'aria-labelledby': `${item.label}-button` }}
      >
        {item.children.map((child) => (
          <MenuItem
            key={child.to}
            component={RouterLink}
            to={child.to}
            selected={isCurrent(location.pathname, child.to)}
            onClick={() => setAnchor(null)}
            sx={{ minHeight: 44, minWidth: 200 }}
          >
            {child.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const close = () => setOpen(false);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      color="inherit"
      sx={{
        backgroundColor: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(16px)',
        borderBottom: '1px solid',
        borderColor: 'rgba(11, 29, 51, 0.08)',
        color: 'text.primary',
      }}
    >
      <Container>
        <Toolbar disableGutters sx={{ minHeight: { xs: 72, md: 82 }, gap: 1, minWidth: 0 }}>
          <Box
            component={RouterLink}
            to="/"
            onClick={close}
            aria-label="NCPL home"
            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none', mr: 'auto', minWidth: 0 }}
          >
            <NcplLogo height={{ xs: 40, md: 44, lg: 56 }} />
          </Box>
          {mobile ? (
            <>
              <Button
                component={RouterLink}
                to={cta.to}
                variant="contained"
                size="small"
                sx={{ minHeight: 42, px: 1.5, display: { xs: 'none', sm: 'inline-flex' }, whiteSpace: 'nowrap' }}
              >
                {cta.label}
              </Button>
              <IconButton
                aria-label={open ? 'Close navigation' : 'Open navigation'}
                aria-expanded={open}
                aria-controls="mobile-navigation"
                onClick={() => setOpen((value) => !value)}
                sx={{ width: 48, height: 48 }}
              >
                <MenuIcon open={open} />
              </IconButton>
              <Drawer anchor="right" open={open} onClose={close} ModalProps={{ keepMounted: false }}>
                <Box
                  id="mobile-navigation"
                  component="nav"
                  aria-label="Mobile navigation"
                  sx={{ width: 'min(84vw, 360px)', p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}
                >
                  <NcplLogo compact />
                  <List sx={{ mt: 2, flexGrow: 1 }}>
                    <ListItemButton component={NavLink} to="/" onClick={close} sx={{ minHeight: 48 }}>
                      Home
                    </ListItemButton>
                    {mainNavigation.map((item) =>
                      item.children ? (
                        <Box key={item.label} sx={{ mt: 1.5 }}>
                          <Typography component="p" sx={{ px: 2, pt: 1, pb: 0.5, fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
                            {item.label}
                          </Typography>
                          {item.children.map((child) => (
                            <ListItemButton
                              key={child.to}
                              component={NavLink}
                              to={child.to}
                              onClick={close}
                              selected={isCurrent(location.pathname, child.to)}
                              sx={{ minHeight: 48 }}
                            >
                              {child.label}
                            </ListItemButton>
                          ))}
                        </Box>
                      ) : (
                        <ListItemButton
                          key={item.to}
                          component={NavLink}
                          to={item.to}
                          onClick={close}
                          selected={isCurrent(location.pathname, item.to)}
                          sx={{ minHeight: 48 }}
                        >
                          {item.label}
                        </ListItemButton>
                      ),
                    )}
                  </List>
                  <Button component={RouterLink} to={cta.to} variant="contained" fullWidth onClick={close} sx={{ mt: 2 }}>
                    {cta.label}
                  </Button>
                </Box>
              </Drawer>
            </>
          ) : (
            <Stack
              component="nav"
              aria-label="Main navigation"
              direction="row"
              alignItems="center"
              sx={{ gap: { md: 1.25, lg: 2.25 } }}
            >
              {mainNavigation.map((item) => (
                <DesktopItem key={item.label} item={item} />
              ))}
              <Button component={RouterLink} to={cta.to} variant="contained" sx={{ ml: 0.5, minHeight: 40, px: 1.75, whiteSpace: 'nowrap' }}>
                {cta.label}
              </Button>
            </Stack>
          )}
        </Toolbar>
      </Container>
    </AppBar>
  );
}
